import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import OpenInNewIcon from '@mui/icons-material/OpenInNewRounded';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import InstitutionIcon from 'components/InstitutionIcon';
import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { institutionsUtils } from 'app-components/flux/institutions';
import { tracker, isDemoMode } from 'app-components/utils/core';

import { useTheme } from '@mui/material/styles';

import ConsentDark from 'assets/images/addFI/ConsentDark.svg';
import ConsentLight from 'assets/images/addFI/ConsentLight.svg';

import { Divider } from '@mui/material';
import styles from './styles';
const useStyles = makeStyles()(styles);

export default function PartnerConsent({ handlePartnerConsent, institution, trackingProperties }) {
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState();

  const theme = useTheme();

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleOpenTerms = () => {
    window.open('https://www.quicken.com/simplifi/terms', '_blank');
  };

  const handleOpenPrivacyPolicy = () => {
    window.open('https://www.quicken.com/privacy', '_blank');
  };

  const [showTooltip, setShowTooltip] = useState(false);
  const onContinueClick = useCallback(() => {
    if (isDemoMode()) {
      setShowTooltip(true);
      return;
    }

    tracker.track(tracker.events.addFISelectContinue, {
      ...trackingProperties,
      aggregator: institution.aggregator,
      channel: institution.fdp?.channelData?.type,
      ...institutionsUtils.getInstitutionTrackingProperties({ institution }),
    });

    handlePartnerConsent?.();
  }, [handlePartnerConsent, institution, trackingProperties]);

  return (
    <>
      <StdDialogContent id='partner_consent_dialog' sx={{ display: 'flex', mt: 4, mx: 3, p: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {theme.palette.mode === 'dark' && <img src={ConsentDark} alt={'consent dark'} />}
          {theme.palette.mode !== 'dark' && <img src={ConsentLight} alt={'consent light'} />}
          <div style={{ marginLeft: '65px', marginTop: '-45px' }}>
            <InstitutionIcon institution={institution} size='xs' />
          </div>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 0 }}>
              <Typography variant='titleMedium' sx={{ my: 3 }}>
                Quicken Simplifi uses Intuit to connect
              </Typography>
              <Typography variant='bodyRegular' sx={{ mb: 3 }} className={classes.secondaryText}>
                We need your consent to access your data.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Accordion
                className={classes.accordion}
                elevation={0}
                expanded={expanded === 'panel1'}
                onChange={handleChange('panel1')}
              >
                <AccordionSummary
                  className={classes.accordionSummary}
                  aria-controls='panel1d-content'
                  id='panel1d-header'
                  expandIcon={<ArrowForwardIosSharpIcon />}
                >
                  <Typography variant='bodyRegular'>Data that&apos;s accessed</Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <Typography variant='paragraphRegular' className={classes.secondaryText}>
                    Simplifi will have access to account details like names, numbers, balances and credit limits.
                  </Typography>
                  <br></br>
                  <Typography variant='paragraphRegular' className={classes.secondaryText}>
                    This also includes amounts, dates, and descriptions of current and historical transactions.
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion
                className={classes.accordion}
                elevation={0}
                expanded={expanded === 'panel2'}
                onChange={handleChange('panel2')}
              >
                <AccordionSummary
                  className={classes.accordionSummary}
                  aria-controls='panel2d-content'
                  id='panel2d-header'
                  expandIcon={<ArrowForwardIosSharpIcon />}
                >
                  <Typography variant='bodyRegular'>Your data is kept private</Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <Typography variant='paragraphRegular' className={classes.secondaryText}>
                    Simplifi uses this data to provide a consolidated view of your finances and forecasts in accordance
                    with our&nbsp;
                    <Typography
                      className={classes.termsLink}
                      component='span'
                      variant='inherit'
                      onClick={handleOpenPrivacyPolicy}
                    >
                      Privacy Policy <OpenInNewIcon fontSize='inherit' />
                    </Typography>
                    .
                  </Typography>
                </AccordionDetails>
              </Accordion>
              <Accordion
                className={classes.accordion}
                elevation={0}
                expanded={expanded === 'panel3'}
                onChange={handleChange('panel3')}
              >
                <AccordionSummary
                  className={classes.accordionSummary}
                  aria-controls='panel3d-content'
                  id='panel3d-header'
                  expandIcon={<ArrowForwardIosSharpIcon />}
                >
                  <Typography variant='bodyRegular'>You&apos;re in control</Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <Typography variant='paragraphRegular' className={classes.secondaryText}>
                    Unlink your accounts from Simplifi at any time by deleting it from your Settings.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Box>

            <Box
              sx={{
                justifySelf: 'flex-end',
                flexDirection: 'column',
                flexGrow: 0,
                pt: 1.5,
              }}
            >
              <Divider />
              <Typography style={{ marginTop: theme.spacing(2) }}>
                By continuing, you consent to let Intuit share your data with Simplifi in line with our&nbsp;
                <Typography className={classes.termsLink} component='span' variant='inherit' onClick={handleOpenTerms}>
                  Terms of Service <OpenInNewIcon fontSize='inherit' />
                </Typography>
                .
              </Typography>
            </Box>
          </Box>
        </Box>
      </StdDialogContent>

      <StdDialogActions
        sx={{ display: 'flex', flexGrow: 0 }}
        primary={{
          children: 'Continue',
          id: 'continueButton',
          onClick: onContinueClick,
          tooltipText: 'Not available in Test Drive.',
          showTooltip,
        }}
      />
    </>
  );
}

PartnerConsent.propTypes = {
  handlePartnerConsent: PropTypes.func.isRequired,
  institution: PropTypes.object.isRequired,
  trackingProperties: PropTypes.object,
};
